import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import GuestBanner from '../components/GuestBanner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Utensils,
  Pill,
  TestTube,
  Cookie,
  Scissors,
  Gamepad2,
  Shirt,
  Plus,
  Search,
  Star,
  ThumbsUp,
  BarChart3,
  Loader2,
  ChevronRight,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  food: Utensils,
  medication: Pill,
  supplement: TestTube,
  treats: Cookie,
  grooming: Scissors,
  toys: Gamepad2,
  accessories: Shirt
};

const ConsumerReports = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [addReviewOpen, setAddReviewOpen] = useState(false);
  const [compareProducts, setCompareProducts] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [saving, setSaving] = useState(false);

  const [filters, setFilters] = useState({
    category: 'all',
    search: '',
    min_rating: 'all'
  });

  const [productForm, setProductForm] = useState({
    name: '',
    brand: '',
    category: 'food',
    pet_types: ['dog'],
    description: '',
    image_url: '',
    ingredients: '',
    price_range: '',
    manufacturer: ''
  });

  const [reviewForm, setReviewForm] = useState({
    overall_rating: 5,
    quality_rating: 5,
    value_rating: 5,
    effectiveness_rating: 5,
    title: '',
    content: '',
    pros: '',
    cons: '',
    would_recommend: true,
    pet_name: '',
    usage_duration: ''
  });

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/api/products`, {
          params: {
            category: filters.category !== 'all' ? filters.category : undefined,
            search: filters.search || undefined,
            min_rating: filters.min_rating !== 'all' ? filters.min_rating : undefined
          }
        }),
        axios.get(`${API}/api/products/categories`)
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductReviews = async (productId) => {
    try {
      const res = await axios.get(`${API}/api/products/${productId}/reviews`);
      setReviews(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSelectProduct = async (product) => {
    setSelectedProduct(product);
    await fetchProductReviews(product.id);
  };

  const handleCreateProduct = async () => {
    if (!productForm.name || !productForm.brand || !productForm.description) {
      toast.error('Please fill required fields');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/products`, productForm);
      toast.success('Product added!');
      setAddProductOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to add product');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateReview = async () => {
    if (!reviewForm.title || !reviewForm.content) {
      toast.error('Please fill required fields');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/products/${selectedProduct.id}/reviews`, {
        ...reviewForm,
        pros: reviewForm.pros.split(',').map(p => p.trim()).filter(Boolean),
        cons: reviewForm.cons.split(',').map(c => c.trim()).filter(Boolean)
      });
      toast.success('Review submitted!');
      setAddReviewOpen(false);
      handleSelectProduct(selectedProduct);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  const toggleCompare = (product) => {
    if (compareProducts.find(p => p.id === product.id)) {
      setCompareProducts(compareProducts.filter(p => p.id !== product.id));
    } else if (compareProducts.length < 4) {
      setCompareProducts([...compareProducts, product]);
    } else {
      toast.error('Max 4 products to compare');
    }
  };

  const CategoryIcon = ({ category }) => {
    const Icon = categoryIcons[category] || Utensils;
    return <Icon className="w-5 h-5" />;
  };

  const RatingStars = ({ rating, size = 'sm' }) => {
    const starSize = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';
    return (
      <div className="flex items-center gap-0.5">
        {[1,2,3,4,5].map(i => (
          <Star key={i} className={`${starSize} ${i <= rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`} />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <GuestBanner message="Sign up to add products and write reviews!" />

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-heading font-medium flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-primary" />
              Consumer Reports
            </h1>
            <p className="text-muted-foreground">
              Compare and review pet foods, medications, and products
            </p>
          </div>
          <div className="flex gap-3">
            {compareProducts.length >= 2 && (
              <Button variant="outline" onClick={() => setShowComparison(true)}>
                <BarChart3 className="w-4 h-4 mr-2" />
                Compare ({compareProducts.length})
              </Button>
            )}
            {user && (
              <Dialog open={addProductOpen} onOpenChange={setAddProductOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-product-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Product
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add Product</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <Input placeholder="Product Name *" value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} />
                    <Input placeholder="Brand *" value={productForm.brand} onChange={(e) => setProductForm({...productForm, brand: e.target.value})} />
                    <Select value={productForm.category} onValueChange={(val) => setProductForm({...productForm, category: val})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Textarea placeholder="Description *" value={productForm.description} onChange={(e) => setProductForm({...productForm, description: e.target.value})} />
                    <Input placeholder="Image URL" value={productForm.image_url} onChange={(e) => setProductForm({...productForm, image_url: e.target.value})} />
                    <Input placeholder="Ingredients (for food/supplements)" value={productForm.ingredients} onChange={(e) => setProductForm({...productForm, ingredients: e.target.value})} />
                    <Input placeholder="Price Range (e.g., $20-30)" value={productForm.price_range} onChange={(e) => setProductForm({...productForm, price_range: e.target.value})} />
                    <Button onClick={handleCreateProduct} className="w-full" disabled={saving}>
                      {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      Add Product
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search products..." value={filters.search} onChange={(e) => setFilters({...filters, search: e.target.value})} className="pl-10" />
          </div>
          <Select value={filters.category} onValueChange={(val) => setFilters({...filters, category: val})}>
            <SelectTrigger className="w-40"><SelectValue placeholder="All Categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.min_rating} onValueChange={(val) => setFilters({...filters, min_rating: val})}>
            <SelectTrigger className="w-32"><SelectValue placeholder="Any Rating" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any Rating</SelectItem>
              <SelectItem value="4">4+ Stars</SelectItem>
              <SelectItem value="3">3+ Stars</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Products List */}
          <div className="lg:col-span-2 space-y-4">
            {products.length === 0 ? (
              <Card className="text-center py-16">
                <BarChart3 className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-heading text-xl mb-2">No products yet</h3>
                <p className="text-muted-foreground">Add the first product!</p>
              </Card>
            ) : (
              products.map(product => (
                <Card 
                  key={product.id} 
                  className={`cursor-pointer transition-all ${selectedProduct?.id === product.id ? 'ring-2 ring-primary' : 'hover:shadow-md'}`}
                  onClick={() => handleSelectProduct(product)}
                  data-testid={`product-${product.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="w-20 h-20 rounded-lg object-cover" />
                      ) : (
                        <div className="w-20 h-20 rounded-lg bg-muted flex items-center justify-center">
                          <CategoryIcon category={product.category} />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-medium">{product.name}</h3>
                            <p className="text-sm text-muted-foreground">{product.brand}</p>
                          </div>
                          <Badge variant="outline" className="capitalize">{product.category}</Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-2">
                          <div className="flex items-center gap-1">
                            <RatingStars rating={Math.round(product.overall_rating)} />
                            <span className="text-sm font-medium">{product.overall_rating.toFixed(1)}</span>
                          </div>
                          <span className="text-sm text-muted-foreground">{product.review_count} reviews</span>
                          {product.recommendation_percentage > 0 && (
                            <span className="text-sm text-green-600">{product.recommendation_percentage}% recommend</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={(e) => { e.stopPropagation(); toggleCompare(product); }}
                          >
                            {compareProducts.find(p => p.id === product.id) ? 'Remove' : 'Compare'}
                          </Button>
                          {product.price_range && <Badge variant="secondary">{product.price_range}</Badge>}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Product Details */}
          <div className="space-y-4">
            {selectedProduct ? (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{selectedProduct.name}</CardTitle>
                    <p className="text-sm text-muted-foreground">{selectedProduct.brand}</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm">{selectedProduct.description}</p>
                    
                    {/* Ratings Breakdown */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>Quality</span>
                        <div className="flex items-center gap-2">
                          <Progress value={selectedProduct.quality_rating * 20} className="w-24 h-2" />
                          <span>{selectedProduct.quality_rating.toFixed(1)}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span>Value</span>
                        <div className="flex items-center gap-2">
                          <Progress value={selectedProduct.value_rating * 20} className="w-24 h-2" />
                          <span>{selectedProduct.value_rating.toFixed(1)}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span>Effectiveness</span>
                        <div className="flex items-center gap-2">
                          <Progress value={selectedProduct.effectiveness_rating * 20} className="w-24 h-2" />
                          <span>{selectedProduct.effectiveness_rating.toFixed(1)}</span>
                        </div>
                      </div>
                    </div>

                    {selectedProduct.ingredients && (
                      <div>
                        <h4 className="text-sm font-medium mb-1">Ingredients</h4>
                        <p className="text-xs text-muted-foreground">{selectedProduct.ingredients}</p>
                      </div>
                    )}

                    {user && (
                      <Dialog open={addReviewOpen} onOpenChange={setAddReviewOpen}>
                        <DialogTrigger asChild>
                          <Button className="w-full">Write Review</Button>
                        </DialogTrigger>
                        <DialogContent className="max-h-[80vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>Review {selectedProduct.name}</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <Input placeholder="Review Title *" value={reviewForm.title} onChange={(e) => setReviewForm({...reviewForm, title: e.target.value})} />
                            <Textarea placeholder="Your review *" value={reviewForm.content} onChange={(e) => setReviewForm({...reviewForm, content: e.target.value})} rows={4} />
                            <div className="grid grid-cols-2 gap-4">
                              {['overall', 'quality', 'value', 'effectiveness'].map(type => (
                                <div key={type}>
                                  <label className="text-sm capitalize">{type}</label>
                                  <Select value={String(reviewForm[`${type}_rating`])} onValueChange={(val) => setReviewForm({...reviewForm, [`${type}_rating`]: parseInt(val)})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      {[5,4,3,2,1].map(n => <SelectItem key={n} value={String(n)}>{n} Stars</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </div>
                              ))}
                            </div>
                            <Input placeholder="Pros (comma-separated)" value={reviewForm.pros} onChange={(e) => setReviewForm({...reviewForm, pros: e.target.value})} />
                            <Input placeholder="Cons (comma-separated)" value={reviewForm.cons} onChange={(e) => setReviewForm({...reviewForm, cons: e.target.value})} />
                            <label className="flex items-center gap-2">
                              <input type="checkbox" checked={reviewForm.would_recommend} onChange={(e) => setReviewForm({...reviewForm, would_recommend: e.target.checked})} />
                              <span className="text-sm">I would recommend this product</span>
                            </label>
                            <Button onClick={handleCreateReview} className="w-full" disabled={saving}>
                              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                              Submit Review
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </CardContent>
                </Card>

                {/* Reviews */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Reviews ({reviews.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {reviews.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">No reviews yet</p>
                    ) : (
                      reviews.map(review => (
                        <div key={review.id} className="border-b pb-4 last:border-0">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <RatingStars rating={review.overall_rating} />
                              <span className="font-medium text-sm">{review.title}</span>
                            </div>
                            {review.would_recommend && <Badge variant="secondary" className="text-xs"><ThumbsUp className="w-3 h-3 mr-1" />Recommends</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{review.content}</p>
                          {review.pros.length > 0 && (
                            <p className="text-xs text-green-600">Pros: {review.pros.join(', ')}</p>
                          )}
                          {review.cons.length > 0 && (
                            <p className="text-xs text-red-600">Cons: {review.cons.join(', ')}</p>
                          )}
                          <p className="text-xs text-muted-foreground mt-2">By {review.username}</p>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="text-center py-16">
                <ChevronRight className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-muted-foreground">Select a product to see details</p>
              </Card>
            )}
          </div>
        </div>

        {/* Comparison Modal */}
        <Dialog open={showComparison} onOpenChange={setShowComparison}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Product Comparison</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${compareProducts.length}, 1fr)` }}>
              {compareProducts.map(p => (
                <Card key={p.id}>
                  <CardContent className="p-4 text-center">
                    <h3 className="font-medium mb-1">{p.name}</h3>
                    <p className="text-sm text-muted-foreground mb-2">{p.brand}</p>
                    <div className="flex justify-center mb-2"><RatingStars rating={Math.round(p.overall_rating)} /></div>
                    <p className="text-lg font-bold">{p.overall_rating.toFixed(1)}</p>
                    <div className="space-y-1 mt-4 text-sm">
                      <p>Quality: {p.quality_rating.toFixed(1)}</p>
                      <p>Value: {p.value_rating.toFixed(1)}</p>
                      <p>Effect: {p.effectiveness_rating.toFixed(1)}</p>
                      <p className="text-muted-foreground">{p.review_count} reviews</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ConsumerReports;
